import { Injectable } from '@angular/core';
import { PointType } from '../../../team-apis/otterdam/answers/shape-typings';
import {
  Attachment,
  ClientFeedback,
  ClientFeedbackType,
} from '../../../team-apis/otterdam/feedback';
import { AbstractResource } from '../../http/abstract-resource';
import {
  clientFeedbackAttachmentsPath,
  clientFeedbackCommentPath,
  clientFeedbackPath,
  clientFeedbackResolveWithComment,
  commentAttachmentsPath,
  createClientFeedbackPath,
} from './client-issue-paths';

export interface CreateIssueCommentRequestOptions {
  text: string;
  attachments?: Attachment[];
}

export interface ClientFeedbackRequestOptions {
  id: string;
  type: ClientFeedbackType;
  title: string;
  description: string;
  tags: string[];
  parkTasks: boolean;
  location: {
    frame_number: number;
    coordinates: PointType;
    output_id: number;
  };
  attachments?: Attachment[];
}

export interface UpdateIssueRequest {
  type: ClientFeedbackType;
  title: string;
  description: string;
  tags: string[];
  location: {
    frame_number: number;
    coordinates: PointType;
    output_id: number;
  };
}

export type Screenshot = {
  blob: Blob;
  url: string;
  name: string;
};

export type ClientFeedbackTag = {
  id: number;
  projectId: number;
  tag: string;
};

@Injectable()
export class ClientFeedbackResource extends AbstractResource {
  createIssue$(
    projectId: number,
    taskId: string,
    body: ClientFeedbackRequestOptions,
  ) {
    return this.request$<ClientFeedback>(
      'POST',
      createClientFeedbackPath(projectId, taskId),
      {
        body,
        skipDefaultErrorMessaging: true,
        tokenScope: 'access_token',
      },
    );
  }

  deleteIssue$(projectId: number, taskId: string, issueId: string) {
    return this.request$<ClientFeedback>(
      'DELETE',
      clientFeedbackPath(projectId, taskId, issueId),
      {
        body: {},
        skipDefaultErrorMessaging: true,
        tokenScope: 'access_token',
      },
    );
  }

  updateIssue$(
    projectId: number,
    taskId: string,
    issueId: string,
    body: UpdateIssueRequest,
  ) {
    return this.request$<ClientFeedback>(
      'PATCH',
      createClientFeedbackPath(projectId, taskId, issueId),
      {
        body,
        skipDefaultErrorMessaging: true,
        tokenScope: 'access_token',
      },
    );
  }

  createComment$(
    projectId: number,
    taskId: string,
    body: CreateIssueCommentRequestOptions,
    issueId: string,
  ) {
    return this.request$<ClientFeedback>(
      'POST',
      clientFeedbackCommentPath(projectId, taskId, issueId),
      {
        body,
        skipDefaultErrorMessaging: true,
        tokenScope: 'access_token',
      },
    );
  }

  resolveIssueWithComment$(
    projectId: number,
    taskId: string,
    commentId: string,
    issueId: string,
  ) {
    return this.request$(
      'POST',
      clientFeedbackResolveWithComment(projectId, taskId, issueId),
      {
        body: { commentId },
        skipDefaultErrorMessaging: true,
        tokenScope: 'access_token',
      },
    );
  }

  uploadAttachmentForClientFeedback$(
    projectId: number,
    taskId: string,
    screenshot: Screenshot,
    issueId: string,
  ) {
    const path = clientFeedbackAttachmentsPath(projectId, taskId, issueId);
    return this.uploadAttachment(path, screenshot);
  }

  uploadAttachmentForComment$(
    projectId: number,
    taskId: string,
    screenshot: Screenshot,
    issueId: string,
  ) {
    const path = commentAttachmentsPath(projectId, taskId, issueId);
    return this.uploadAttachment(path, screenshot);
  }

  private uploadAttachment(path: string, screenshot: Screenshot) {
    return this.request$<{
      downloadUrl: string;
      uploadUrl: string;
      objectKey: string;
    }>('POST', path, {
      body: {
        mode: 'file',
        file: {
          src: screenshot.url,
        },
      },
      skipDefaultErrorMessaging: true,
      tokenScope: 'access_token',
    });
  }
}
